// src/pages/Auditoria/AuditoriaHome.js
//
// Pantalla independiente de Auditoría — usada por Director/Subdirector de
// Ejecuciones para SUPERVISAR el flujo. La mayoría de los planes ya se
// auto-auditan al pasar a expediente (commit del 4-jun "Fase 4"). La pantalla
// queda como panel de monitoreo + acciones excepcionales sobre los legacy.
//
// Permiso: "PLANES DE PAGO - AUDITORIA / DESVINCULACION DE FORMULARIOS"
// en módulo "EJECUCIONES TRIBUTARIAS Y OTROS REQUERIMIENTOS DE CRÉDITOS FISCALES".

import React, { useState } from 'react';
import {
    Container, Box, Paper, Typography, Stack, Alert,
    Divider,
} from '@mui/material';
import {
    FactCheck as AuditoriaIcon,
    CheckCircle as CheckCircleIcon,
    PendingActions as PendingIcon,
    ErrorOutline as ErrorIcon,
    ChevronRight as ChevronRightIcon,
    InfoOutlined as InfoIcon,
} from '@mui/icons-material';

import { usePermisos } from '../../utils/usePermisos';
import AuditoriaAdminTable from '../Honorarios/AuditoriaAdminTable';

const COLOR = '#37474f'; // gris azulado (paleta Panel de Firma — sobrio, de supervisión)
const BR = 4;

export default function AuditoriaHome() {
    const { tienePermiso } = usePermisos();
    // Acepta cualquiera de las 2 funciones que legacy y nuevo usan
    // historicamente (ver comentario en Sidebar.js).
    const puedeVer = (
        tienePermiso('PLANES DE PAGO - AUDITORIA / DESVINCULACION DE FORMULARIOS') ||
        tienePermiso('PLANES DE PAGO Y REGIMEN LEY 1504-I AUDITAR PLANES')
    );

    // Stats vivientes (los recibe la tabla embebida via callback)
    const [stats, setStats] = useState({ auditado: 0, pendiente: 0, incompleto: 0 });
    // Filtros vivientes (la tabla notifica)
    const [filtros, setFiltros] = useState({ estado: '0' });
    // Filtro de estado que aplicamos desde el padre (click en pipeline)
    const [filtroEstadoExterno, setFiltroEstadoExterno] = useState(undefined);

    if (!puedeVer) {
        return (
            <Box sx={{ p: 4 }}>
                <Alert severity="error">
                    No tenés permiso para ver Auditoría. Esta pantalla la usan Director y Subdirector de Ejecuciones.
                    Pedile a un administrador el permiso <strong>"PLANES DE PAGO - AUDITORIA / DESVINCULACION DE FORMULARIOS"</strong>.
                </Alert>
            </Box>
        );
    }

    const total = stats.auditado + stats.pendiente + stats.incompleto;
    const items = [];
    if (stats.pendiente > 0) {
        items.push({
            key: 'pend', icon: <PendingIcon sx={{ color: '#e65100' }} />, color: '#e65100',
            label: `${stats.pendiente.toLocaleString('es-AR')} planes "Pendientes" (legacy)`,
            sub: 'Planes vinculados a expediente que aún no se auto-auditaron. Antes de la Fase 4 (4-jun) no había auto-auditado, así que estos esperan revisión manual.',
            onClick: () => { setFiltroEstadoExterno('0'); },
        });
    }
    if (stats.incompleto > 0) {
        items.push({
            key: 'inc', icon: <ErrorIcon sx={{ color: '#c62828' }} />, color: '#c62828',
            label: `${stats.incompleto.toLocaleString('es-AR')} planes "Incompletos"`,
            sub: 'Sin expediente vinculado todavía. El cron diario los vincula automáticamente; si querés forzarlo ahora, andá a Planes (DGR) → "Vincular ahora".',
            onClick: () => { setFiltroEstadoExterno('-1'); },
        });
    }

    return (
        <Box sx={{ bgcolor: '#f6f8fc', minHeight: '100vh', py: 2, px: { xs: 2, md: 3 } }}>
            <Container maxWidth={false} disableGutters>
                {/* ── Header ────────────────────────────────────────────────── */}
                <Stack direction="row" alignItems="center" sx={{ mb: 2, gap: 1.5, flexWrap: 'wrap' }}>
                    <AuditoriaIcon sx={{ color: COLOR, fontSize: 36 }} />
                    <Box sx={{ flexGrow: 1 }}>
                        <Typography variant="h5" sx={{ fontWeight: 700, color: COLOR }}>
                            Auditoría de Ejecuciones
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            Supervisión y aprobación de planes vinculados a expediente
                        </Typography>
                    </Box>
                </Stack>

                {/* ── Pipeline (3 cards) ────────────────────────────────────── */}
                <Box sx={{ display: 'grid', gap: 1.5, gridTemplateColumns: { xs: '1fr', md: '1fr 32px 1fr 32px 1fr' }, alignItems: 'stretch', mb: 2 }}>
                    <PipelineCard
                        num="1" icon={<CheckCircleIcon />} titulo="AUTO-AUDITADOS" color="#2e7d32"
                        valorPrincipal={stats.auditado.toLocaleString('es-AR')}
                        valorLabel="aprobados automáticamente"
                        lineas={[
                            { label: 'Pct del total', valor: total > 0 ? `${Math.round(stats.auditado / total * 100)}%` : '—' },
                            { label: 'Estado', valor: filtros.estado === '1' ? '✓ filtrando' : 'click para filtrar' },
                        ]}
                        activo={filtros.estado === '1'}
                        onClick={() => setFiltroEstadoExterno('1')}
                    />
                    <FlechaPipeline />
                    <PipelineCard
                        num="2" icon={<PendingIcon />} titulo="PENDIENTES (LEGACY)" color={stats.pendiente > 0 ? '#e65100' : '#999'}
                        valorPrincipal={stats.pendiente.toLocaleString('es-AR')}
                        valorLabel="esperan revisión manual"
                        lineas={[
                            { label: 'Origen', valor: 'planes pre-Fase 4' },
                            { label: 'Acción', valor: 'auditar selección' },
                        ]}
                        activo={filtros.estado === '0'}
                        onClick={() => setFiltroEstadoExterno('0')}
                    />
                    <FlechaPipeline />
                    <PipelineCard
                        num="3" icon={<ErrorIcon />} titulo="INCOMPLETOS" color={stats.incompleto > 0 ? '#c62828' : '#999'}
                        valorPrincipal={stats.incompleto.toLocaleString('es-AR')}
                        valorLabel="sin expediente vinculado"
                        lineas={[
                            { label: 'Vincula', valor: 'cron diario' },
                            { label: 'Forzar', valor: 'Planes (DGR) →' },
                        ]}
                        activo={filtros.estado === '-1'}
                        onClick={() => setFiltroEstadoExterno('-1')}
                    />
                </Box>

                {/* ── Atención ──────────────────────────────────────────────── */}
                {items.length > 0 && (
                    <Paper sx={{ p: 1.5, mb: 2, borderRadius: `${BR}px`, borderLeft: '4px solid #e65100' }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5, color: '#666' }}>
                            ⚠ Requieren tu atención ({items.length})
                        </Typography>
                        <Stack divider={<Divider />} spacing={0}>
                            {items.map(it => (
                                <AtencionItem key={it.key} icon={it.icon} color={it.color}
                                    label={it.label} sub={it.sub} onClick={it.onClick} />
                            ))}
                        </Stack>
                    </Paper>
                )}

                {/* ── Nota explicativa del nuevo flujo ─────────────────────── */}
                {items.length === 0 && (
                    <Alert severity="success" icon={<InfoIcon />} sx={{ mb: 2, borderRadius: `${BR}px` }}>
                        <strong>Todo en orden.</strong> No hay planes pendientes de revisión manual ni incompletos.
                        Los planes nuevos se auto-auditan al pasar a expediente; esta pantalla queda para supervisar
                        y para los casos excepcionales (legacy y desvinculaciones).
                    </Alert>
                )}

                {/* ── Tabla unificada (AuditoriaAdminTable embebida) ───────── */}
                <Paper sx={{ borderRadius: `${BR}px`, p: 0, overflow: 'hidden' }}>
                    <AuditoriaAdminTable
                        modoEmbebido
                        filtroEstadoExterno={filtroEstadoExterno}
                        onStatsChange={setStats}
                        onFiltersChange={setFiltros}
                    />
                </Paper>
            </Container>
        </Box>
    );
}

// ============================================================================
// Componentes visuales (mismo patrón que rediseños DGR y Conciliación)
// ============================================================================
function PipelineCard({ num, icon, titulo, color, valorPrincipal, valorLabel, lineas = [], activo, onClick }) {
    return (
        <Paper variant="outlined" onClick={onClick}
            sx={{
                p: 2, borderRadius: `${BR}px`, cursor: onClick ? 'pointer' : 'default',
                borderTop: `3px solid ${color}`,
                transition: 'all 0.15s',
                boxShadow: activo ? `0 0 0 2px ${color}55` : undefined,
                transform: activo ? 'translateY(-2px)' : undefined,
                '&:hover': onClick ? { boxShadow: 2, transform: 'translateY(-2px)' } : {},
                display: 'flex', flexDirection: 'column', gap: 1,
            }}>
            <Stack direction="row" alignItems="center" spacing={1}>
                <Box sx={{ bgcolor: color, color: 'white', width: 24, height: 24, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 13 }}>
                    {num}
                </Box>
                <Box sx={{ color, display: 'flex' }}>{icon}</Box>
                <Typography variant="caption" sx={{ fontWeight: 700, color: '#666', letterSpacing: 0.5 }}>
                    {titulo}
                </Typography>
            </Stack>
            <Box sx={{ pl: 0.5 }}>
                <Typography variant="h4" sx={{ fontWeight: 800, color, lineHeight: 1.1 }}>
                    {valorPrincipal}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                    {valorLabel}
                </Typography>
            </Box>
            {lineas.length > 0 && (
                <Stack spacing={0.25} sx={{ pt: 0.5, borderTop: '1px dashed #eee' }}>
                    {lineas.map((l, i) => (
                        <Box key={i} sx={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.78rem' }}>
                            <Typography variant="caption" color="text.secondary">{l.label}</Typography>
                            <Typography variant="caption" sx={{ fontWeight: 600 }}>{l.valor}</Typography>
                        </Box>
                    ))}
                </Stack>
            )}
        </Paper>
    );
}

function FlechaPipeline() {
    return (
        <Box sx={{
            display: { xs: 'none', md: 'flex' }, alignItems: 'center', justifyContent: 'center',
            color: '#bbb',
        }}>
            <ChevronRightIcon sx={{ fontSize: 32 }} />
        </Box>
    );
}

function AtencionItem({ icon, color, label, sub, onClick }) {
    return (
        <Box onClick={onClick} sx={{
            display: 'flex', alignItems: 'center', gap: 1.5, py: 1, px: 0.5,
            cursor: 'pointer', borderRadius: 1,
            '&:hover': { bgcolor: 'rgba(0,0,0,0.03)' },
        }}>
            <Box sx={{ display: 'flex' }}>{icon}</Box>
            <Box sx={{ flexGrow: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 600, color }}>{label}</Typography>
                <Typography variant="caption" color="text.secondary">{sub}</Typography>
            </Box>
            <ChevronRightIcon sx={{ color: '#999' }} />
        </Box>
    );
}
